/**
 * Libelles du back-office de plateforme.
 *
 * Un seul endroit, comme dans les autres applications : le francais ne se
 * disperse pas dans le code. Voir docs/10-conventions-de-code.md.
 */

export const labels = {
  common: {
    close: 'Fermer',
    more: "Plus d'actions",
  },

  confirm: {
    confirm: 'Confirmer',
    abort: 'Revenir',
  },

  nav: {
    label: 'Navigation de la plateforme',
    heading: 'Plateforme',
    restaurants: 'Restaurants',
    openMenu: 'Ouvrir la navigation',
    closeMenu: 'Fermer la navigation',
  },

  app: {
    name: 'Scan2Eat',
    subtitle: 'Plateforme',
    signOut: 'Se déconnecter',
  },

  auth: {
    restoring: 'Ouverture de votre espace...',
    brandTitle: 'Ouvrez un restaurant en une minute.',
    brandText: 'Créez le restaurant et son premier gérant, suivez leur activité, suspendez un compte si besoin.',
    brandPoints: [
      'Le gérant reçoit un mot de passe provisoire à changer.',
      'Un restaurant suspendu ne perd aucune donnée.',
      'Les factures émises ne sont jamais supprimées.',
    ],
    title: 'Back-office plateforme',
    intro: 'Réservé aux administrateurs de la plateforme.',
    email: 'Adresse email',
    password: 'Mot de passe',
    submit: 'Se connecter',
    submitting: 'Connexion en cours',
    invalid: 'Adresse ou mot de passe incorrect.',
    notPlatformAdmin:
      "Ce compte administre un restaurant, pas la plateforme. Utilisez le back-office du restaurant.",
    generic: 'Une erreur est survenue. Réessayez dans un instant.',
  },

  /** Apparence du menu d'un restaurant, tenue par la plateforme. Voir docs/adr/0026. */
  branding: {
    title: 'Apparence du menu',
    subtitle: (nom: string) => `La palette que les clients de ${nom} verront sur leur menu.`,
    currentSection: 'Palette actuelle',
    noPalette: "Aucune palette : le menu de ce restaurant utilise le thème Scan2Eat.",
    removePalette: 'Retirer la palette',
    removing: 'Retrait en cours',
    removeTitle: 'Revenir au thème Scan2Eat ?',
    removeMessage:
      "Le menu de ce restaurant reprend les couleurs par défaut. Son logo et la photo de sa salle sont conservés.",
    removed: 'Palette retirée. Le menu est revenu au thème Scan2Eat.',
    generateSection: 'Générer une palette',
    generateHelp:
      "Décrivez l'ambiance du restaurant, ajoutez les images qu'il vous a transmises, puis générez trois propositions.",
    promptLabel: "Décrivez l'ambiance du restaurant",
    promptPlaceholder: 'Ex. Ambiance chaleureuse, cuisine béninoise, bois et terracotta',
    logoSlot: 'Logo',
    logoHint: 'Devient le logo officiel du restaurant.',
    roomSlot: 'Photo de la salle',
    roomHint: "Affichée en fond de l'en-tête du menu de ses clients.",
    designSlot: 'Image de design',
    designHint: "Une inspiration. Elle n'est pas conservée.",
    generate: 'Générer trois propositions',
    generating: 'Génération en cours…',
    proposalsTitle: 'Propositions',
    apply: 'Choisir cette palette',
    applying: 'Application…',
    applied: 'Palette appliquée au menu de ce restaurant.',
    manualSection: 'Choisir les couleurs à la main',
    manualHelp: "Quand la charte du restaurant est déjà connue, sans passer par l'IA.",
    primaryColor: 'Couleur principale',
    backgroundColor: 'Fond',
    textColor: 'Texte',
    accentColor: 'Boutons et mise en avant',
    applyManual: 'Appliquer ces couleurs',
    sampleText: 'Aperçu du texte',
    sampleAccent: 'Bouton',
    generationFailed: "La génération n'a pas abouti. Réessayez dans un instant.",
    uploadFailed: "Le téléversement de l'image a échoué.",
    close: 'Fermer',
  },

  restaurants: {
    title: 'Restaurants',
    subtitle: 'Ouvrez, modifiez et suspendez les restaurants de la plateforme.',
    openHelp: 'Le restaurant et son premier gérant sont créés ensemble.',
    restaurantSection: 'Restaurant',
    kpiTotal: 'Restaurants',
    kpiOrders: 'Commandes passées',
    tablesColumn: 'Tables',
    ordersColumn: 'Commandes',
    settingsColumn: 'Devise et TVA',
    statusColumn: 'Statut',
    taxRate: (rate: string) => `TVA ${rate} %`,
    actionsFor: (name: string) => `Actions pour ${name}`,
    editTitle: 'Modifier le restaurant',
    address: 'Adresse',
    phone: 'Téléphone',
    taxPermille: 'TVA en pour mille',
    taxHint: '180 pour 18,0 %. Entier, pour ne jamais manipuler de flottant.',
    copy: 'Copier',
    copied: 'Copié',
    suspendTitle: (name: string) => `Suspendre ${name} ?`,
    suspendHelp: "Ses QR codes cesseront de répondre et son personnel ne pourra plus se connecter. Rien n'est perdu : vous pourrez le remettre en service.",
    removeTitle: (name: string) => `Supprimer ${name} définitivement ?`,
    removeHelp: 'Son gérant, son personnel et ses tables disparaissent avec lui. Cette action est irréversible.',
    resetTitle: (email: string) => `Réinitialiser le mot de passe de ${email} ?`,
    resetHelp: "Son mot de passe actuel cessera de fonctionner immédiatement. Le nouveau ne sera affiché qu'une fois.",
    openTitle: 'Ouvrir un restaurant',
    open: 'Ouvrir le restaurant',
    opening: 'Ouverture en cours',
    listTitle: 'Restaurants ouverts',
    empty: 'Aucun restaurant pour le moment.',
    name: 'Nom du restaurant',
    slug: 'Identifiant',
    slugHint: 'Minuscules, chiffres et tirets. Sert dans les adresses, et ne change plus ensuite.',
    currency: 'Devise',
    tax: 'TVA',
    regime: 'Régime fiscal',
    regimeReel: 'Réel — assujetti à la TVA, 18 %',
    regimeTps: 'TPS — sous le seuil de TVA, 0 %',
    regimeHint:
      "Les deux régimes émettent des factures normalisées ; ils ne diffèrent que par la TVA portée. L'information vient du restaurateur ou de son comptable, pas de nous : dans le doute, demandez avant d'ouvrir.",
    regimeShort: { REEL: 'Réel', TPS: 'TPS' },
    regimeTpsLocked: 'Régime TPS : le taux reste à 0, et ne se modifie pas.',
    ownerSection: 'Premier gérant',
    ownerHint:
      'Son compte est créé avec le restaurant : sans lui, personne ne pourrait administrer la carte ni les tables.',
    firstName: 'Prénom',
    lastName: 'Nom',
    ownerEmail: 'Adresse email',
    paymentTitle: 'Encaissement mobile money',
    paymentNone: "Aucune clé PayDunya enregistrée. Ce restaurant n'encaisse pas : ses clients paient au comptoir, ou annoncent un paiement que le personnel vérifie.",
    paymentConfigured: 'Clés enregistrées',
    paymentMode: 'Mode',
    paymentModeTest: 'Test',
    paymentModeLive: 'Production',
    paymentModeHint:
      "Test frappe le bac à sable PayDunya, Production encaisse pour de vrai. Les clés de test ne fonctionnent qu'en mode Test.",
    paymentKeysHint:
      "Les trois clés du restaurateur, telles qu'il nous les a transmises. Elles sont éprouvées auprès de PayDunya avant enregistrement, chiffrées en base, et ne seront plus jamais réaffichées.",
    paymentMasterKey: 'Master Key',
    paymentPrivateKey: 'Private Key',
    paymentToken: 'Token',
    paymentSave: 'Vérifier et enregistrer',
    paymentChecking: 'Vérification auprès de PayDunya',
    paymentConfigure: 'Configurer l\'encaissement',
    paymentReplace: 'Remplacer les clés',
    paymentRemove: 'Retirer les clés',
    paymentRemoveConfirm: (name: string) =>
      `Retirer les clés de ${name} ? Il repassera en vérification manuelle des paiements.`,
    paymentRejected:
      "PayDunya refuse ces clés. Vérifiez qu'elles viennent bien de la même application, et que le mode correspond.",
    paymentUnavailable:
      "PayDunya ne répond pas. Les clés n'ont pas été enregistrées ; réessayez dans un moment.",
    openedTitle: (name: string) => `${name} est ouvert`,
    openedHelp:
      'Communiquez ces identifiants au gérant. Le mot de passe provisoire ne sera plus jamais affiché : notez-le maintenant. Il devra le changer à sa première connexion.',
    temporaryPassword: 'Mot de passe provisoire',
    noted: 'C\'est noté',
    loadError: 'Impossible de charger les restaurants.',

    owner: 'Gérant',
    noOwner: 'Aucun gérant',
    tables: (n: number) => `${n} table${n > 1 ? 's' : ''}`,
    orders: (n: number) => `${n} commande${n > 1 ? 's' : ''}`,
    active: 'En service',
    suspended: 'Suspendu',

    edit: 'Modifier',
    save: 'Enregistrer',
    saving: 'Enregistrement',
    cancel: 'Annuler',
    suspend: 'Suspendre',
    reactivate: 'Remettre en service',
    resetPassword: 'Réinitialiser le mot de passe',
    remove: 'Supprimer',

    confirmReactivate: (name: string) => `Remettre ${name} en service ?`,

    resetDoneTitle: (email: string) => `Nouveau mot de passe pour ${email}`,
    resetDoneHelp:
      "Communiquez-le au gérant. Il ne sera plus jamais affiché, et il devra le changer à sa première connexion.",

    cannotRemove: 'Ce restaurant porte des commandes, donc des factures.',
    removeHint: 'Suspendez-le : ses pièces comptables doivent survivre.',
  },
} as const;
